import React, { useContext } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Text, Image } from "./base/index";
// eslint-disable-next-line import/no-named-as-default
import { PageStateContext } from "../PageContext";
import { createStatusInfo } from "../utils/contentStatusUtils";
import { createHeadingNavProps } from "../utils/createToc";
import { debugLog, renderHtmlString } from "../utils/index";

const tooltipClsName = item =>
  createStatusInfo(item.changedType).status
    ? "edited-success tooltip mb-2"
    : "edited-error tooltip mb-2";

/**
 * Creates consistent TOC links for cover page that match the createToc approach
 *
 * @param {Object} chapter - Chapter object with id and title
 * @param {number} index - Chapter index for uniqueness
 * @returns {Object} Navigation props with href and id
 */
const createCoverTocLink = (chapter, index) => {
  try {
    // Use the same logic as HeadingWrapper and createToc for consistency
    const navProps = createHeadingNavProps(
      chapter.title,
      "chapter",
      chapter.id || `chapter-${index + 1}`
    );

    debugLog(`Created cover TOC link for chapter: ${chapter.title}`, navProps);
    return navProps;
  } catch (error) {
    debugLog(
      `Error creating cover TOC link for chapter: ${chapter.title}`,
      error
    );
    // Fallback to a basic approach if createHeadingNavProps fails
    const fallbackId = `chapter-${chapter.id || index + 1}`;
    return {
      hasValidLink: true,
      id: fallbackId,
      href: `#${fallbackId}`,
    };
  }
};

/**
 * Enhanced Cover component with proper TOC links that match createToc approach
 */
const Cover = ({ chapters = [], shouldUsePaginator = true }) => {
  const { pageState } = useContext(PageStateContext);

  if (!pageState || !pageState.chrome) {
    return (
      <div className="header-loading">
        <span>Loading Cover...</span>
      </div>
    );
  }

  const { chrome, uiText, formattedDate } = pageState;
  const { title = "Cover Page", htmlTitle, logo, color = "#000000" } = chrome;
  const hasChapter = chapters && chapters.length > 0;

  const renderedTitle =
    pageState.isPreview && htmlTitle ? renderHtmlString(htmlTitle) : title;
  debugLog("Cover component rendering with chapters:", chapters?.length || 0);
  debugLog("shouldUsePaginator:", shouldUsePaginator);

  return (
    <div
      className={`cover flex flex-col justify-center ${
        shouldUsePaginator ? "ml-page-margin" : ""
      } max-w-content`}
    >
      {logo && (
        <Image
          image={logo}
          className={classNames({
            "mb-10": true,
            "-mt-20": !hasChapter,
          })}
        />
      )}

      <Text
        tag={hasChapter ? "label" : "h2"}
        className="mb-4"
        style={{ color }}
      >
        {renderedTitle}
      </Text>

      {hasChapter && (
        <>
          <Text tag="h2" className="capitalize mb-4">
            {uiText.screen_manual_selector} {uiText.print_user_manual}
          </Text>
          <Text tag="span" className="text-gray-2 mb-10">
            {formattedDate}
          </Text>

          {/**
           * ENHANCED FALLBACK TOC with proper href links
           * This now generates the same href anchors that createToc methods use,
           * ensuring consistency between paginated and non-paginated modes
           */}
          <div id="my-toc-fallback">
            <ul
              className="list-cover-toc"
              style={{ listStyle: "none", paddingLeft: "0" }}
            >
              {chapters.map((item, index) => {
                // Generate consistent navigation props
                const navProps = createCoverTocLink(item, index);

                return (
                  <li key={item.id || index} style={{ margin: "10px 0" }}>
                    {navProps.hasValidLink ? (
                      <a
                        href={navProps.href}
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                          display: "inline-block",
                          padding: "2px 0",
                        }}
                        onClick={e => {
                          // Enhanced click handling for better UX
                          e.preventDefault();

                          // Try to find the target element
                          const targetElement = document.getElementById(
                            navProps.id
                          );
                          if (targetElement) {
                            // Smooth scroll to target with offset
                            const offset = 80;
                            const elementPosition = targetElement.getBoundingClientRect()
                              .top;
                            const offsetPosition =
                              elementPosition + window.scrollY - offset;

                            window.scrollTo({
                              top: offsetPosition,
                              behavior: "smooth",
                            });

                            // Update URL hash
                            if (window.history && window.history.pushState) {
                              window.history.pushState(
                                null,
                                null,
                                navProps.href
                              );
                            }
                            debugLog(
                              `Navigated to chapter: ${item.title} via cover TOC`
                            );
                          } else {
                            debugLog(
                              `Target element not found for chapter: ${item.title}, ID: ${navProps.id}`
                            );
                            // Fallback: try to scroll to the element by href
                            const fallbackTarget = document.querySelector(
                              navProps.href
                            );
                            if (fallbackTarget) {
                              fallbackTarget.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                            }
                          }
                        }}
                      >
                        <Text
                          tag="span"
                          use="p"
                          options={{ font: "font-header" }}
                          className={
                            item.changedType ? tooltipClsName(item) : "mb-2"
                          }
                          style={{ margin: 0 }} // Remove default margins for inline display
                        >
                          {item.changedType && item.changedData
                            ? item.changedData.entryName
                            : item.title}
                          {item.changedType && (
                            <span className="tooltiptext">
                              {createStatusInfo(item.changedType).msg}
                            </span>
                          )}
                        </Text>
                      </a>
                    ) : (
                      // Fallback for items without valid links
                      <Text
                        tag="span"
                        use="p"
                        options={{ font: "font-header" }}
                        className={
                          item.changedType ? tooltipClsName(item) : "mb-2"
                        }
                      >
                        {item.changedType && item.changedData
                          ? item.changedData.entryName
                          : item.title}
                        {item.changedType && (
                          <span className="tooltiptext">
                            {createStatusInfo(item.changedType).msg}
                          </span>
                        )}
                      </Text>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

Cover.propTypes = {
  chapters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      changedType: PropTypes.number,
      changedData: PropTypes.object,
    }).isRequired
  ),
  shouldUsePaginator: PropTypes.bool,
};

export default Cover;
